/**
 * Rhythmic patterns, used for rhythm dictations.
 */
import { Fraction } from "../utils/math/fraction";
import { getRandomItem } from "../utils/array-utils";
import { AssessmentOLD } from "./content";

/**
 * A rhythmic score.
 */
export class RhythmScore {

	/**
	 * The notes in this score and their assessments.
	 */
	notes: AssessedTime[] = []

	/** The tempo in beats per minute. */
	tempo: Bpm = 80

	/** Quarter beats per measure. */
	timeBeats: number = RhythmScore.timeBeatsFixed
	private static timeBeatsFixed = 4

	constructor(public measures: RhythmMeasure[]) {
		for (let measure = 0; measure < measures.length; measure++) {
			let beat = _0
			for (let pattern of measures[measure].patterns) {
				for (let duration of pattern.durations) {
					this.notes.push(new AssessedTime(measure + beat.toNumber()))
					beat = beat.plus(duration)
				}
			}
		}
	}

	/**
	 * Creates a score with random rhythmic patterns with the given length in measures.
	 */
	static randomScore(measuresCount: number) {
		let measures: RhythmMeasure[] = []
		for (let i = 0; i < measuresCount; i++) {
			let patterns: RhythmPattern[] = []
			let remainingDur = new Fraction(RhythmScore.timeBeatsFixed, 4)
			let isLastMeasure = i == measuresCount - 1
			while (remainingDur.compareTo(_0) > 0) {
				let pattern: RhythmPattern
				if (isLastMeasure) //in last measure, only use 1/4, 1/2, 3/4 and 1/1 figures
					pattern = RhythmPatterns.randomPattern(remainingDur, RhythmPatterns.quarterPatterns)
				else //else: random pattern
					pattern = RhythmPatterns.randomPattern(remainingDur)
				patterns.push(pattern)
				remainingDur = remainingDur.minus(pattern.duration)
			}
			measures.push(new RhythmMeasure(patterns))
		}
		return new RhythmScore(measures)
	}

	/**
	 * Converts the given time in milliseconds into a SimpleTime.
	 */
	msToTime(ms: number): SimpleTime {
		return ms / 1000 * this.tempo / 60 / this.timeBeats
	}

	timeToMs(time: SimpleTime): number {
		return 1000 * time * this.timeBeats * 60 / this.tempo
	}

}

/**
 * A rhythmic measure.
 */
export class RhythmMeasure {

	constructor(public patterns: RhythmPattern[]) {
	}

	/**
	 * The total duration of the measure.
	 */
	get duration(): Duration {
		return this.patterns.reduce((acc, pattern) => acc.plus(pattern.duration), _0)
	}
}

/**
 * A basic rhythmic pattern.
 */
export class RhythmPattern {

	/**
	 * @param {Duration[]} durations  the duration of each note
	 * @param {PatternImage} id       the ID of the corresponding image
	 * @param {number} probability    the probability that the pattern is chosen in a random rhythm
	 * @param {string} name           the localized name of the pattern
	 */
	constructor(public durations: Duration[],
	            public id: PatternImage,
	            public probability: number,
	            public name: string) {
	}

	/**
	 * The total duration of the pattern.
	 */
	get duration(): Duration {
		return this.durations.reduce((acc, dur) => acc.plus(dur), _0)
	}

	withProbability(probability: number) {
		return new RhythmPattern(this.durations, this.id, probability, this.name)
	}

}

/**
 * Note durations.
 */

//duration type is Fraction
export type Duration = Fraction
export const _0 = new Fraction(0, 1)

//normal notes
export const _1$16 = new Fraction(1, 16)
export const _1$8 = new Fraction(1, 8)
export const _1$4 = new Fraction(1, 4)
export const _1$2 = new Fraction(1, 2)
export const _1$1 = new Fraction(1, 1)

//dotted notes
export const _1$8_dot = new Fraction(3, 16)
export const _1$4_dot = new Fraction(3, 8)
export const _1$2_dot = new Fraction(3, 4)

//triplets
export const _1$8_tri = new Fraction(1, 12)
export const _1$4_tri = new Fraction(1, 6)

/** The ID of the image which shows the notes of the pattern. */
export type PatternImage = string

/**
 * Available basic rhythmic patterns.
 */
export class RhythmPatterns {

	/**
	 * 1/1, 3/4, 1/2, 1,4.
	 */
	static pattern_1$1 = new RhythmPattern([_1$1], "1", 0.3, "𝅝")
	static pattern_1$2_dot = new RhythmPattern([_1$2_dot], "2dot", 0.4, "𝅗𝅥.")
	static pattern_1$2 = new RhythmPattern([_1$2], "2", 0.5, "𝅗𝅥")
	static pattern_1$4 = new RhythmPattern([_1$4], "4", 0.3, "♩")
	static quarterPatterns = [
		RhythmPatterns.pattern_1$1,
		RhythmPatterns.pattern_1$2_dot,
		RhythmPatterns.pattern_1$2,
		RhythmPatterns.pattern_1$4,
	]

	/**
	 * All available patterns.
	 */
	static allPatterns = [
		//1/1 duration
		RhythmPatterns.pattern_1$1.withProbability(0.05),
		//3/4 duration
		RhythmPatterns.pattern_1$2_dot.withProbability(0.2),
		new RhythmPattern([_1$8, _1$4, _1$4, _1$8], "8-4-4-8", 0.1, "♪♩ ♩ ♪"),
		//1/2 duration
		RhythmPatterns.pattern_1$2.withProbability(0.2),
		new RhythmPattern([_1$4_dot, _1$8], "4dot-8", 0.2, "♩.♪"),
		new RhythmPattern([_1$8, _1$4, _1$8], "8-4-8", 0.1, "♪♩ ♪"),
		new RhythmPattern([_1$4_tri, _1$4_tri, _1$4_tri], "4tri-4tri-4tri", 0.03, "♩-Triolen"),
		//1/4 duration
		RhythmPatterns.pattern_1$4.withProbability(0.3),
		new RhythmPattern([_1$8, _1$8], "8-8", 0.3, "♫"),
		new RhythmPattern([_1$8_dot, _1$16], "8dot-16", 0.1, "♪.𝅘𝅥𝅯"),
		new RhythmPattern([_1$16, _1$16, _1$8], "16-16-8", 0.05, "♬♪"),
		new RhythmPattern([_1$8, _1$16, _1$16], "8-16-16", 0.05, "♪♬"),
		new RhythmPattern([_1$16, _1$8, _1$16], "16-8-16", 0.05, "𝅘𝅥𝅯♪𝅘𝅥𝅯"),
		new RhythmPattern([_1$16, _1$16, _1$16, _1$16], "16-16-16-16", 0.05, "♬♬"),
		new RhythmPattern([_1$8_tri, _1$8_tri, _1$8_tri], "8tri-8tri-8tri", 0.05, "♪-Triolen"),
	]

	/**
	 * Returns a random pattern with the given maximum duration.
	 */
	static randomPattern(maxDur: Duration, allowedPatterns: RhythmPattern[] = RhythmPatterns.allPatterns) {
		for (let i = 0; i < 1000; i++) {
			let pattern = getRandomItem(allowedPatterns)
			if (pattern.duration.compareTo(maxDur) <= 0 && Math.random() <= pattern.probability)
				return pattern
		}
		//should not happen. if it does, select first that matches
		let ret = allowedPatterns.find(it => it.duration.compareTo(maxDur) <= 0)
		if (ret == null)
			throw Error("No pattern found for maximum duration " + maxDur)
		return ret
	}

}

/**
 * Score time with an assessment.
 */
export class AssessedTime {

	assessment: AssessmentOLD = null

	constructor(public time: SimpleTime) {
	}

	get matched() {
		return this.assessment != null
	}

}

/**
 * Instead of expressing a time by a measure index and beat (as a Fraction),
 * it can also be expressed by a number. The integer part is the measure index, the
 * decimal part the position within the measure. For example, 0.5 is in the middle of
 * measure 0, while 2.0 is at the beginning of measure 2.
 */
export type SimpleTime = number

/**
 * Tempo in beats per minute.
 */
export type Bpm = number


// WEBPACK FOOTER //
// ./src/model/rhythm.ts